/**
 * CleanerDashboard — list of tasks assigned to the current cleaner.
 * Each row links to the Task Detail page for that task.
 */

import { type ReactNode } from 'react'
import { Link }            from 'react-router'
import { useTranslation }  from 'react-i18next'
import { useMyTasks, type MyTaskRow }                 from '@/hooks/useMyTasks'
import { useAvailableTasks, type AvailableTaskRow }   from '@/hooks/useAvailableTasks'
import { taskDetailPath }  from '@/router/paths'

export function CleanerDashboard(): ReactNode {
  const { t } = useTranslation()
  const { data, isLoading, error } = useMyTasks()
  const available                  = useAvailableTasks()

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <header>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
            {t('dashboard.myTasks')}
          </h1>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)' }}>
            {t('dashboard.tapToStart')}
          </p>
        </header>

        {isLoading && <Hint>{t('dashboard.loadingTasks')}</Hint>}
        {error    && <ErrorPanel message={error instanceof Error ? error.message : t('dashboard.failedToLoadTasks')} />}

        {!isLoading && !error && (
          (data && data.length > 0)
            ? <TaskList rows={data} />
            : <Hint>{t('dashboard.noTasksAssigned')}</Hint>
        )}
      </section>

      <AvailableJobsSection
        data={available.data}
        isLoading={available.isLoading}
        error={available.error instanceof Error ? available.error : null}
      />
    </section>
  )
}

function AvailableJobsSection({
  data,
  isLoading,
  error,
}: {
  data:      AvailableTaskRow[] | undefined
  isLoading: boolean
  error:     Error | null
}): ReactNode {
  const { t } = useTranslation()
  return (
    <section data-testid="cd-available" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <header>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('dashboard.availableJobs')}
        </h2>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)', fontSize: '0.9rem' }}>
          {t('dashboard.tapToTake')}
        </p>
      </header>

      {isLoading && <Hint>{t('dashboard.loadingTasks')}</Hint>}
      {error    && <ErrorPanel message={error.message} />}

      {!isLoading && !error && (
        (data && data.length > 0)
          ? <AvailableList rows={data} />
          : <Hint>{t('dashboard.noAvailableJobs')}</Hint>
      )}
    </section>
  )
}

function AvailableList({ rows }: { rows: AvailableTaskRow[] }): ReactNode {
  const { t } = useTranslation()
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {rows.map((row) => (
        <li key={row.id}>
          <Link
            to={taskDetailPath(row.id)}
            data-testid={`cd-available-row-${row.id}`}
            style={{
              display:        'block',
              padding:        '0.75rem 1rem',
              background:     'var(--brand-white)',
              border:         '1px dashed var(--brand-accent)',
              borderRadius:   'var(--brand-radius-md)',
              textDecoration: 'none',
              color:          'var(--brand-neutral-900)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>{t('dashboard.room', { number: row.room?.room_number ?? '—' })}</strong>
              <span
                style={{
                  fontSize:     '0.75rem',
                  padding:      '0.1rem 0.5rem',
                  borderRadius: 'var(--brand-radius-full)',
                  background:   'var(--brand-accent-subtle)',
                  color:        'var(--brand-accent)',
                  fontWeight:   600,
                }}
              >
                {t('dashboard.takeBadge')}
              </span>
            </div>
            <div style={{ marginTop: '0.25rem', color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
              {t('dashboard.clean', { type: row.cycle_type })}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function TaskList({ rows }: { rows: MyTaskRow[] }): ReactNode {
  const { t } = useTranslation()
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {rows.map((row) => (
        <li key={row.id}>
          <Link
            to={taskDetailPath(row.id)}
            style={{
              display:        'block',
              padding:        '0.75rem 1rem',
              background:     'var(--brand-white)',
              border:         '1px solid var(--brand-neutral-300)',
              borderRadius:   'var(--brand-radius-md)',
              textDecoration: 'none',
              color:          'var(--brand-neutral-900)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>{t('dashboard.room', { number: row.room?.room_number ?? '—' })}</strong>
              <span
                style={{
                  fontSize:     '0.75rem',
                  padding:      '0.1rem 0.5rem',
                  borderRadius: 'var(--brand-radius-full)',
                  background:   'var(--brand-primary-subtle)',
                  color:        'var(--brand-primary)',
                  textTransform:'capitalize',
                }}
              >
                {row.status.replace(/_/g, ' ')}
              </span>
            </div>
            <div style={{ marginTop: '0.25rem', color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
              {t('dashboard.clean', { type: row.cycle_type })}
              {row.started_at && <> · {t('dashboard.started', { date: new Date(row.started_at).toLocaleString() })}</>}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function Hint({ children }: { children: ReactNode }): ReactNode {
  return (
    <p style={{ color: 'var(--brand-neutral-500)' }}>{children}</p>
  )
}

function ErrorPanel({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.75rem 1rem',
        fontSize:     '0.9rem',
      }}
    >
      {message}
    </div>
  )
}
